import { React, useState, useRef, useEffect } from "react";
import { db, storage } from "../firebase/firebase";
import {
  collection,
  setDoc,
  doc,
  getDocs,
  where,
  query,
} from "firebase/firestore";
import Button from "react-bootstrap/Button";
import "bootstrap/dist/css/bootstrap.min.css";
import { ref, uploadBytes } from "firebase/storage";
import { ToastService } from "../components/Toast";
import Form from "react-bootstrap/Form";
import "./FileUpload.css";
import { majorScales, minorScales } from "../assets/scales";
import validate from "validate.js";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../contexts/authContext/Index";
import { useNavigate } from "react-router-dom";
import { transliterateGreekToEnglish } from "../components/Helpers";
import { hymnCategories } from "../assets/hymnCategories";
import { PDFDocument, rgb } from "pdf-lib";
import {
  cleanHtmlFile,
  convertDocxToHtml,
  buildHymnHtml,
  parseHymnHtml,
} from "../components/hymnHtml";
import { Tabs, Tab } from "react-bootstrap";
import HymnEditor from "../components/HymnEditor";

function UploadPreview({ file, cleanHtml, onHtmlReady }) {
  const [bodyHtml, setBodyHtml] = useState("");

  // NOTE: `onHtmlReady` must be a STABLE reference (e.g. a useState setter or a
  // useCallback-wrapped function). If a caller passes an inline arrow, this effect
  // re-fires every render and creates an infinite loop with the setBodyHtml below.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const isDocx =
          file.name.endsWith(".docx") ||
          file.type ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
        const htmlFile = isDocx ? await convertDocxToHtml(file) : file;
        const cleaned = cleanHtml ? await cleanHtmlFile(htmlFile) : htmlFile;
        const fullHtml = await cleaned.text();
        const body = parseHymnHtml(fullHtml);
        if (!cancelled) {
          setBodyHtml(body);
          onHtmlReady(body);
        }
      } catch (err) {
        console.error("UploadPreview parse error", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [file, cleanHtml, onHtmlReady]);

  if (!bodyHtml) return <div>Φόρτωση προεπισκόπησης…</div>;
  return (
    <HymnEditor
      initialHtml={bodyHtml}
      onChange={onHtmlReady}
    />
  );
}

export const FileUpload = () => {
  const [showToastService, setShowToastService] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedNotesFile, setSelectedNotesFile] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState("");
  const [selectedScale, setSelectedScale] = useState("0");
  const [selectedNumber, setSelectedNumber] = useState("");
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [stripPdfHeaderFooter, setStripPdfHeaderFooter] = useState(true);
  const [cleanHtml, setCleanHtml] = useState(true);

  const [activeTab, setActiveTab] = useState("write");
  const [editorHtml, setEditorHtml] = useState("");
  const editorRef = useRef(null);

  const fileInputRef = useRef(null);
  const fileNotesInputRef = useRef(null);
  const navigate = useNavigate();
  const { userLoggedIn } = useAuth();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (
      file &&
      (file.type === "text/html" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        file.name.endsWith(".docx") ||
        file.name.endsWith(".html"))
    ) {
      setSelectedFile(e.target.files[0]);
    } else {
      showToastMessage("Παρακαλώ επιλέξτε αρχείο σε μορφή html ή docx.");
    }
  };

  const handleNotesFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setSelectedNotesFile(e.target.files[0]);
    } else {
      showToastMessage("Παρακαλώ επιλέξτε αρχείο σε μορφή pdf.");
    }
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;

    if (!title) {
      setSelectedTitle(title);
      return;
    }

    // More permissive validation - allow Greek characters (including accents), spaces, common punctuation, and numbers
    // Allow parentheses, apostrophes (all types), commas, periods, dashes, and other common punctuation
    // \u0370-\u03FF: Greek and Coptic block
    // \u1F00-\u1FFF: Greek Extended block (for accents and combined characters)
    // \s: whitespace
    // \u0027: straight apostrophe '
    // \u2018: left single quotation mark '
    // \u2019: right single quotation mark '
    // \-,.(): common punctuation
    // 0-9: numbers
    const constraints = {
      title: {
        format: /^[\u0370-\u03FF\u1F00-\u1FFF\s\-,.()\u0027\u2018\u20190-9]+$/,
      },
    };
    const validation = validate({ title }, constraints);

    if (!validation) {
      setSelectedTitle(title);
    } else {
      showToastMessage(
        "Ο τίτλος μπορεί να περιέχει μόνο ελληνικούς χαρακτήρες, αριθμούς, κενά και κοινά σημεία στίξης (.,'-())"
      );
    }
  };

  const handleNumberChange = (e) => {
    setSelectedNumber(e.target.value);
  };

  const handleScaleChange = (e) => {
    setSelectedScale(e.target.value);
    let selected = majorScales
      .concat(minorScales)
      .find((option) => option.value === e.target.value);
    if (selected) setSelectedImage(selected.image);
  };

  const handleUpload = async () => {
    if (!selectedTitle) {
      showToastMessage("Παρακαλώ επιλέξτε όνομα ύμνου");
      return;
    } else if (selectedScale === "0") {
      showToastMessage("Παρακαλώ επιλέξτε τονικότητα");
      return;
      // } else if (!selectedCategory) {
      //   showToastMessage("Παρακαλώ επιλέξτε κατηγορία");
      //   return;
    } else if (
      !selectedFile &&
      !selectedNotesFile &&
      !(activeTab === "write" && editorHtml.trim())
    ) {
      showToastMessage("Παρακαλώ γράψτε ή ανεβάστε τουλάχιστον έναν ύμνο");
      return;
    } else {
      // Convert Greek to English
      const cleanTitle = transliterateGreekToEnglish(selectedTitle);

      // Check if title already exists
      try {
        const hymnQuerySnapshot = await getDocs(
          query(collection(db, "hymns"), where("title", "==", selectedTitle))
        );

        if (!hymnQuerySnapshot.empty) {
          showToastMessage("Ο τίτλος υπάρχει ήδη στη βάση δεδομένων");
          return;
        }
      } catch (error) {
        console.error("Error checking for existing title:", error);
        showToastMessage(
          "Σφάλμα κατά τον έλεγχο του τίτλου. Παρακαλώ δοκιμάστε ξανά."
        );
        return;
      }
      let fileId = "";
      let notesFileId = "";

      if (
        selectedFile ||
        (activeTab === "write" && editorHtml.trim())
      ) {
        fileId = `${cleanTitle}_chords_${uuidv4()}.html`;

        try {
          let fileToUpload;

          if (activeTab === "write") {
            // editorHtml is kept in sync by HymnEditor's onChange prop.
            const fullHtml = buildHymnHtml(editorHtml);
            fileToUpload = new Blob([fullHtml], { type: "text/html" });
          } else {
            // Upload tab: if UploadPreview has populated editorHtml (always, after parse),
            // wrap it with the canonical shell. Falls back to the raw pipeline only if the
            // parse failed for some reason.
            if (editorHtml && editorHtml.trim()) {
              const fullHtml = buildHymnHtml(editorHtml);
              fileToUpload = new Blob([fullHtml], { type: "text/html" });
            } else {
              let processedFile = selectedFile;
              const isDocx =
                selectedFile.name.endsWith(".docx") ||
                selectedFile.type ===
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
              if (isDocx) {
                processedFile = await convertDocxToHtml(selectedFile);
              }
              fileToUpload = cleanHtml
                ? await cleanHtmlFile(processedFile)
                : processedFile;
            }
          }

          const storagechordsRef = ref(storage, `chords/${fileId}`);
          await uploadBytes(storagechordsRef, fileToUpload);
        } catch (error) {
          console.error("Error processing file:", error);
          showToastMessage(
            error.message || "Σφάλμα κατά την επεξεργασία του αρχείου"
          );
          return;
        }
      }

      if (selectedNotesFile) {
        try {
          const fileToUpload = stripPdfHeaderFooter
            ? await removeHeaderFooterFromPdf(selectedNotesFile)
            : selectedNotesFile;

          notesFileId = `${cleanTitle}_notes_${uuidv4()}.pdf`;

          const storagenotesNotesRef = ref(storage, `notes/${notesFileId}`);
          await uploadBytes(storagenotesNotesRef, fileToUpload);
        } catch (err) {
          showToastMessage("Σφάλμα κατά το ανέβασμα του PDF");
          return;
        }
      }

      //add it to hymns collection
      const myCollection = collection(db, "hymns");
      const hymnData = {
        title: selectedTitle,
        scale: selectedScale,
        number: selectedNumber,
        fileId: fileId,
        notesFileId: notesFileId,
        category: selectedCategory,
        visible: true,
      };

      // Define the document reference
      const myDocRef = doc(myCollection, selectedTitle);
      await setDoc(myDocRef, hymnData);

      clearFileInput();
      setSelectedTitle("");
      setSelectedScale("");
      setSelectedNumber("");
      setSelectedNotesFile();
      showToastMessage("Επιτυχία Ανεβάσματος");
    }
  };

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setSelectedFile(null);
    setEditorHtml("");
    if (editorRef.current) {
      editorRef.current.setHTML("");
    }
  };

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToastService(!showToastService);
    setTimeout(() => {
      setShowToastService(false);
    }, 1500);
  };

  const removeHeaderFooterFromPdf = async (
    file,
    headerHeight = 50,
    footerHeight = 50
  ) => {
    const buffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(buffer);
    const pages = pdfDoc.getPages();

    pages.forEach((page) => {
      const { width, height } = page.getSize();

      // Draw white rectangle over header
      page.drawRectangle({
        x: 0,
        y: height - headerHeight,
        width: width,
        height: headerHeight,
        color: rgb(1, 1, 1),
      });

      // Draw white rectangle over footer
      page.drawRectangle({
        x: 0,
        y: 0,
        width: width,
        height: footerHeight,
        color: rgb(1, 1, 1),
      });
    });

    const pdfBytes = await pdfDoc.save();
    return new File([pdfBytes], file.name, { type: "application/pdf" });
  };

  return (
    <div
      className="file-upload-container"
      style={{ maxHeight: "90vh", overflowY: "auto", paddingBottom: "5rem" }}
    >
      <h4 className="text-center font-bold">Προσθήκη Νέου Ύμνου</h4>
      <div className="text-center text-xl pt-5">
        <div className=" pt-2 mx-auto">
          <Form.Label>Τίτλος</Form.Label>
          <Form.Control
            type="text"
            value={selectedTitle}
            onChange={handleTitleChange}
            placeholder="Όνομα τίτλου"
          />
        </div>
        <div className="mx-auto pt-4 text-start">
          <Form.Label>Στίχοι με συγχορδίες</Form.Label>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab eventKey="write" title="Γράψε">
              <HymnEditor
                ref={editorRef}
                initialHtml={editorHtml}
                onChange={setEditorHtml}
              />
            </Tab>
            <Tab eventKey="upload" title="Ανέβασε αρχείο">
              <Form.Control
                type="file"
                accept=".html,.docx,text/html,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              <Form.Check
                type="checkbox"
                label="Καθαρισμός HTML"
                checked={cleanHtml}
                onChange={(e) => setCleanHtml(e.target.checked)}
                className="pt-2"
              />
              {selectedFile && (
                <div className="pt-3">
                  <Form.Label>Προεπισκόπηση / διορθώσεις</Form.Label>
                  <UploadPreview
                    file={selectedFile}
                    cleanHtml={cleanHtml}
                    onHtmlReady={setEditorHtml}
                  />
                </div>
              )}
            </Tab>
          </Tabs>
        </div>
        <div className="mx-auto pt-4">
          <Form.Label>Αρχείο με νότες (προαιρετικά)</Form.Label>
          <Form.Control
            type="file"
            ref={fileNotesInputRef}
            onChange={handleNotesFileChange}
          />
          <Form.Check
            type="checkbox"
            label="Strip pdf"
            checked={stripPdfHeaderFooter}
            onChange={(e) => setStripPdfHeaderFooter(e.target.checked)}
            className="pt-2"
          />
        </div>
        {/* <div className="mx-auto pt-4">
          <Form.Label>Αριθμός Ύμνου (προαιρετικά)</Form.Label>
          <Form.Control
            type="number"
            min="1"
            value={selectedNumber}
            onChange={handleNumberChange}
            placeholder="Αριθμός"
          />
        </div> */}
        <div className="w-60 pt-4 pb-4 mx-auto">
          <Form.Label>Κατηγορία Ύμνου</Form.Label>
          <Form.Select
            aria-label="Επιλογή κατηγορίας"
            value={selectedCategory}
            onChange={handleCategoryChange}
          >
            <option value="">Επιλέξτε κατηγορία</option>
            {hymnCategories.map((category, index) => (
              <option key={index} value={category}>
                {category}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="w-60 pt-4 pb-4 mx-auto">
          <Form.Label>Τονικότητα</Form.Label>
          <Form.Select
            aria-label="Επιλογή τονικότητας"
            value={selectedScale}
            onChange={handleScaleChange}
            className="scale-select"
          >
            <option value="0">Επιλέξτε τονικότητα</option>
            <optgroup label="Μείζονες (Major)">
              {majorScales.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="Ελάσσονες (Minor)">
              {minorScales.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          </Form.Select>
          {selectedImage && (
            <div className="scale-preview pt-3">
              <img
                className="mx-auto d-block"
                src={selectedImage}
                alt="Scale preview"
                style={{ maxWidth: "100%", height: "auto", maxHeight: "200px" }}
              />
            </div>
          )}
        </div>
        <Button variant="primary" onClick={handleUpload}>
          Ανέβασμα
        </Button>
        {showToastService && <ToastService message={toastMessage} />} {}
      </div>
    </div>
  );
};
